/**
 * RBAC-4.1 · Change someone's role from their own record (FR-30, FR-34, UX-DR15).
 *
 * Pure, no DOM, no Supabase. The rows come from `person_role_assignment()` and the change goes
 * through `assign_person_role()` (`20261101d3_rbac_4_1_assign_person_role.sql`) via
 * `services/personRoleService.ts`. The screen is `components/settings/components/users/PersonRoleCard.tsx`.
 *
 * ⚠️ THE DATABASE DECIDES; THIS ONLY SAYS SO FIRST. Self-change, privileged roles and RBAC-1.5's
 * reserved set are refused by the `users` and `user_roles` triggers, which the RPC runs under the
 * caller's identity. `cannotChangeSentence()` and `roleChangePreview().blocked` foresee those
 * refusals so an admin reads them before confirming rather than after; they enforce nothing.
 *
 * ⚠️ A CHANGE REPLACES, IT NEVER ADDS (FR-34). `users.role_id` is one column and holds one role for
 * almost everyone (579 of 637 people hold nothing else, measured 2026-11-01), so the one-role
 * sentence is the normal case, not a warning for an edge case.
 */

import { areaSummaries, type AccountRole, type AreaSummary } from './accountRoles';
import { duplicateRoleErrorMessage, type RpcErrorLike } from './roleDuplication';
import { listOf, permissionTitle } from './rolePermissionDiff';

export const PERSON_ROLE_ASSIGNMENT_RPC = 'person_role_assignment';
export const ASSIGN_PERSON_ROLE_RPC = 'assign_person_role';

/** `legacy`: users.role_id only. `mirrored`: plus its own copy in user_roles. `multiple`: more than one role. */
export type RoleSystem = 'legacy' | 'mirrored' | 'multiple';

/** Where a held role comes from. `other_account` never names the account. */
export type RoleOrigin = 'primary' | 'account' | 'parent_account' | 'other_account';

export type CannotChangeReason = 'self' | 'privileged';

export interface HeldRole {
  roleId: number;
  name: string;
  isGlobal: boolean;
  isPrivileged: boolean;
  origin: RoleOrigin;
  parentAccountName: string | null;
}

export interface PersonRoleAssignment {
  userId: string;
  firstName: string;
  lastName: string;
  isActive: boolean;
  /** `users.role_id`: the role the change replaces, and the one it is binding on. */
  primaryRoleId: number | null;
  system: RoleSystem;
  roles: HeldRole[];
  canChange: boolean;
  cannotChangeReason: CannotChangeReason | null;
}

const SYSTEMS: readonly RoleSystem[] = ['legacy', 'mirrored', 'multiple'];
const ORIGINS: readonly RoleOrigin[] = ['primary', 'account', 'parent_account', 'other_account'];

const num = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : null;
};

/** The RPC's jsonb → view model. Anything it does not recognise is null, never a guess. */
export function parsePersonRoleAssignment(raw: unknown): PersonRoleAssignment | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.user_id !== 'string') return null;
  if (!SYSTEMS.includes(r.system as RoleSystem)) return null;
  const reason = r.cannot_change_reason;
  const roles: HeldRole[] = (Array.isArray(r.roles) ? r.roles : [])
    .map((e: Record<string, unknown>) => ({
      roleId: num(e?.role_id),
      name: String(e?.name ?? ''),
      isGlobal: e?.is_global === true,
      isPrivileged: e?.is_privileged === true,
      origin: e?.origin as RoleOrigin,
      parentAccountName: typeof e?.parent_account_name === 'string' ? e.parent_account_name : null,
    }))
    .filter((e): e is HeldRole => e.roleId !== null && ORIGINS.includes(e.origin));
  return {
    userId: r.user_id,
    firstName: typeof r.first_name === 'string' ? r.first_name : '',
    lastName: typeof r.last_name === 'string' ? r.last_name : '',
    isActive: r.is_active !== false,
    primaryRoleId: num(r.primary_role_id),
    system: r.system as RoleSystem,
    roles,
    canChange: r.can_change === true,
    cannotChangeReason: reason === 'self' || reason === 'privileged' ? reason : null,
  };
}

const firstNameOf = (a: Pick<PersonRoleAssignment, 'firstName'>) => a.firstName.trim() || 'This person';

/** The roles a change replaces: their role and any extra role held in their own right here. */
const replaceable = (a: PersonRoleAssignment) => a.roles.filter((r) => r.origin === 'primary' || r.origin === 'account');
/** Untouched by a change: derived from a parent account, or held in another one. */
const untouched = (a: PersonRoleAssignment) => a.roles.filter((r) => r.origin === 'parent_account' || r.origin === 'other_account');

export function originLabel(role: Pick<HeldRole, 'origin' | 'parentAccountName'>): string {
  switch (role.origin) {
    case 'primary':
      return 'Their role';
    case 'account':
      return 'An extra role in this account';
    case 'parent_account':
      return `From ${role.parentAccountName?.trim() || 'a parent account'}, where they are an Agency Admin`;
    case 'other_account':
      return 'Held in another account';
  }
}

/** FR-34 / UX-DR15: which role system they are on, said before anything is chosen. */
export function roleSystemStatement(a: PersonRoleAssignment): string {
  const name = firstNameOf(a);
  const mine = replaceable(a);
  if (mine.length === 0) return `${name} has no role yet. The one you choose becomes their role.`;
  if (a.system !== 'multiple') {
    return `${name} can hold one role at a time. Changing it here replaces ${mine[0].name} rather than adding to it.`;
  }
  const kept = untouched(a).length;
  const keptSentence =
    kept === 0 ? '' : kept === 1 ? ' The 1 that comes from another account stays.' : ` The ${kept} that come from another account stay.`;
  return `${name} holds ${a.roles.length} roles. Changing their role here replaces ${listOf(mine.map((r) => r.name))} with the one you choose.${keptSentence}`;
}

export function cannotChangeSentence(a: Pick<PersonRoleAssignment, 'canChange' | 'cannotChangeReason'>): string | null {
  if (a.canChange) return null;
  if (a.cannotChangeReason === 'self') return "You can't change your own role. Ask another administrator in this account.";
  return 'Only a Joules superadmin can change the role of someone who holds Superadmin or Agency Admin.';
}

/**
 * What the picker offers: every role the account can see, the account's own first, minus privileged
 * roles (only a superadmin may give one, and the trigger refuses anyone else) and minus the role they
 * already hold alone. Someone holding several keeps their current role on offer, because choosing it
 * is how an admin drops the others.
 */
export function changeTargets(roles: readonly AccountRole[], a: PersonRoleAssignment): AccountRole[] {
  const onlyRole = a.system !== 'multiple' ? a.primaryRoleId : null;
  const byName = (x: AccountRole, y: AccountRole) => x.name.localeCompare(y.name);
  const offered = roles.filter((r) => !r.isPrivileged && r.id !== onlyRole);
  return [...offered.filter((r) => !r.isGlobal).sort(byName), ...offered.filter((r) => r.isGlobal).sort(byName)];
}

export interface RoleChangePreview {
  title: string;
  kind: string;
  holders: string;
  /** Areas where the role grants something, with the rows it grants, in words. */
  areas: AreaSummary[];
  /** Said instead of areas when there are none to list. */
  emptySummary: string | null;
  replaces: string;
  /** Non-null when the database will refuse this (RBAC-1.5): the confirm button is disabled. */
  blocked: string | null;
  confirmLabel: string;
}

/** AC 1: the target role's holder count and what it reaches, before confirming. */
export function roleChangePreview(input: {
  assignment: PersonRoleAssignment;
  target: AccountRole;
  /** `reserved_permissions_not_held(target)` for the caller. */
  reservedMissing: readonly string[];
}): RoleChangePreview {
  const { assignment: a, target, reservedMissing } = input;
  const name = firstNameOf(a);
  const n = target.holderCount;
  const holders =
    n <= 0 ? 'Nobody in this account holds it yet.' : n === 1 ? '1 person in this account holds it.' : `${n} people in this account hold it.`;

  const override = target.permissions.includes('system.override');
  const areas = override ? [] : areaSummaries(target.permissions).filter((s) => s.held.length > 0);
  const emptySummary = override ? 'Everything, by override.' : areas.length === 0 ? 'This role gives no permissions this screen shows.' : null;

  const gone = replaceable(a).filter((r) => r.roleId !== target.id).map((r) => r.name);
  const kept = untouched(a).length;
  let replaces: string;
  if (replaceable(a).length === 0) {
    replaces = `${target.name} becomes their role.`;
  } else {
    const after = kept === 0 ? `${target.name} only` : `${target.name} and the ${kept === 1 ? '1 role that comes' : `${kept} roles that come`} from another account`;
    replaces = `This replaces ${listOf(gone)}. ${name} will hold ${after}.`;
  }

  const blocked =
    reservedMissing.length === 0
      ? null
      : `You can't give this role. It carries ${listOf(reservedMissing.map(permissionTitle))}, which you do not hold. Only someone who holds ${reservedMissing.length === 1 ? 'it' : 'them'} can give it.`;

  return {
    title: `Change ${name}'s role to ${target.name}?`,
    kind: target.isGlobal ? 'Joules role' : "Your account's own role",
    holders,
    areas,
    emptySummary,
    replaces,
    blocked,
    confirmLabel: `Change to ${target.name}`,
  };
}

/** Binding: the role the dialog showed them holding. A stale one is refused 22023. */
export function assignPersonRoleArgs(userId: string, roleId: number, a: Pick<PersonRoleAssignment, 'primaryRoleId'>) {
  return { p_user_id: userId, p_role_id: roleId, p_expected_role_id: a.primaryRoleId };
}

/** The server's sentences are written for the admin: keep them and their hint. */
export function assignPersonRoleErrorMessage(err: RpcErrorLike | null | undefined): string {
  if (!err?.message?.trim()) return 'The role was not changed. Check your connection and try again.';
  return duplicateRoleErrorMessage(err);
}

export function isStaleRole(err: RpcErrorLike | null | undefined): boolean {
  return err?.code === '22023' && /changed since you opened it/.test(err.message ?? '');
}

/** P0002 "Person not found.": their record belongs to another account (e.g. an agency grant holder). */
export function personNotHomedHere(err: RpcErrorLike | null | undefined): boolean {
  return err?.code === 'P0002' && /^Person not found/.test(err.message ?? '');
}
